import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { KpiCard } from "@/components/dashboard/shared/KpiCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/utils/api";
import { useOnboardingCheck } from "@/hooks/useOnboardingCheck";
import { FullPageLoader } from "@/components/ui/full-page-loader";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";
import {
  RiShoppingCartLine,
  RiArchiveLine,
  RiArrowRightLine,
  RiAddLine,
  RiPulseLine,
  RiErrorWarningLine,
  RiEyeLine,
  RiFileCopyLine,
  RiLink,
  RiArrowRightUpBoxFill,
  RiArrowRightDownBoxFill,
  RiFileTextLine,
  RiShoppingCart2Line,
  RiGroup3Line,
  RiBarChart2Line,
  RiFileAddLine,
  RiStackLine,
  RiStore3Line,
} from "@remixicon/react";
import { InvoiceListResponse } from "@/types/invoice";
import { Link } from "react-router-dom";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { UpgradeBanner } from "@/components/dashboard/UpgradeBanner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  type DashboardPeriod,
  getDashboardDateRange,
  getDashboardPeriodOptions,
} from "@/lib/dashboard-period";
import { statusBadgeClass } from "@/lib/status";
import { SetupChecklist } from "@/components/dashboard/SetupChecklist";

const quickActions = [
  { label: "Add Product", href: "/dashboard/products/new", icon: RiAddLine, permission: "products" },
  { label: "Inventory", href: "/dashboard/inventory", icon: RiStackLine, permission: "inventory" },
  { label: "POS Mode", href: "/dashboard/pos", icon: RiStore3Line, permission: "pos" },
  { label: "Orders", href: "/dashboard/orders", icon: RiShoppingCart2Line, permission: "orders" },
  { label: "Customers", href: "/dashboard/customers", icon: RiGroup3Line, permission: "customers" },
  { label: "Analytics", href: "/dashboard/analytics", icon: RiBarChart2Line, permission: "analytics" },
  { label: "New Invoice", href: "/dashboard/invoices/new", icon: RiFileAddLine, permission: "invoices" },
  { label: "Invoices", href: "/dashboard/invoices", icon: RiFileTextLine, permission: "invoices" },
] as const;

type RecentOrder = {
  id: string;
  orderNumber: string;
  customer: string;
  amount: number;
  status: string;
  date: string;
};

type TopProduct = {
  id: string;
  name: string;
  sales: number;
  revenue: number;
};

type SalesDataPoint = {
  date: string;
  revenue: number;
  orders: number;
};

type PerformanceData = {
  salesTimeline: SalesDataPoint[];
  averageOrderValue: number;
  conversionRate: number;
  growthRate: number;
  totalRevenue: number;
  totalOrders: number;
};

export function DashboardPage() {
  // Check if onboarding is complete
  const { isOnboardingComplete } = useOnboardingCheck();
  const { currentStore, storeId } = useCurrentStore();
  const { user } = useAuth();
  const { hasPermission } = useStore();
  const [statsPeriod, setStatsPeriod] = useState<DashboardPeriod>("month");
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">(
    "idle",
  );

  const statsDateRange = getDashboardDateRange(statsPeriod);
  const periodOptions = getDashboardPeriodOptions(user?.createdAt);

  const {
    data: stats,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["dashboard-stats", storeId, statsPeriod],
    queryFn: async () => {
      try {
        const { data } = await api.get("/api/analytics/dashboard/stats", {
          params: {
            startDate: statsDateRange.startDate,
            endDate: statsDateRange.endDate,
          },
        });
        return data;
      } catch (error) {
        console.error("Dashboard stats error:", error);
        throw error;
      }
    },
    enabled: isOnboardingComplete && !!storeId,
    retry: 2,
    retryDelay: 1000,
  });

  const { data: performance, isLoading: perfLoading } =
    useQuery<PerformanceData>({
      queryKey: ["dashboard-performance", storeId],
      queryFn: async () => {
        const { data } = await api.get("/api/analytics/dashboard/performance");
        return data;
      },
      enabled: isOnboardingComplete && !!storeId,
      retry: 2,
      retryDelay: 1000,
    });

  const { data: recentInvoices, isLoading: invoicesLoading } =
    useQuery<InvoiceListResponse>({
      queryKey: ["invoices", storeId, "dashboard-recent"],
      queryFn: async () => {
        const { data } = await api.get("/api/invoices", {
          params: { page: 1, limit: 5 },
        });
        return data;
      },
      enabled: isOnboardingComplete && !!storeId && hasPermission("invoices"),
      retry: 1,
    });

  const visibleQuickActions = quickActions.filter((action) =>
    hasPermission(action.permission),
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: currentStore?.currency || "NGN",
    }).format(amount);
  };

  const storeLink = currentStore?.url
    ? `https://sqale.shop/${currentStore.url}`
    : "";
  const hasShareableStore = Boolean(storeLink);

  const handleCopyLink = async () => {
    if (!hasShareableStore) return;
    try {
      await navigator.clipboard.writeText(storeLink);
      setCopyStatus("copied");
    } catch (error) {
      console.error("Failed to copy store link:", error);
      setCopyStatus("error");
    }
  };

  useEffect(() => {
    if (copyStatus === "idle") return;
    const timer = setTimeout(() => setCopyStatus("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyStatus]);

  // If hook is handling the redirect, show a minimal loading state
  if (!isOnboardingComplete) {
    return <FullPageLoader text="Loading your dashboard" variant="contained" />;
  }

  const getStatusColor = statusBadgeClass;

  // Error state
  if (error) {
    return (
      <div className="space-y-6 relative">
        <DashboardHead pageName="Dashboard" />
        <PageHeader
          title="Dashboard"
          subtitle="Here's what's happening with your store today"
        />

        <Alert variant="destructive">
          <RiErrorWarningLine className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard data. Please try again.
            <Button
              variant="link"
              className="p-0 h-auto font-normal underline ml-2"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = user?.name?.trim().split(/\s+/)[0];

  return (
    <div className="mx-auto space-y-6 relative">
      <DashboardHead pageName="Dashboard" />
      <PageHeader
        title={firstName ? `${greeting}, ${firstName}` : greeting}
        subtitle={
          currentStore?.storeName
            ? `Here's how ${currentStore.storeName} is doing.`
            : "Here's how your store is doing."
        }
        actions={
          <Select
            value={statsPeriod}
            onValueChange={(value) => setStatsPeriod(value as DashboardPeriod)}
          >
            <SelectTrigger
              size="sm"
              className={dashboardStyles.pillTrigger}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" className="rounded-xl">
              {periodOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {storeId && !isLoading && stats ? (
        <SetupChecklist
          storeId={storeId}
          storeLink={storeLink}
          hasLogo={Boolean(currentStore?.logo)}
          hasProducts={(stats?.totalProducts ?? 0) > 0}
          hasTraffic={(stats?.storeViews ?? 0) > 0 || (stats?.totalOrders ?? 0) > 0}
          canEditStore={currentStore?.isOwner === true || hasPermission("settings")}
          canAddProducts={hasPermission("products")}
        />
      ) : null}

      <UpgradeBanner />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Card
                  key={i}
                  className="min-h-[148px] rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 shadow-none"
                >
                  <div className="flex h-[148px] flex-col">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="mt-4 h-8 w-20" />
                    <Skeleton className="mt-auto h-4 w-16" />
                  </div>
                </Card>
              ))
            ) : (
              <>
                <KpiCard
                  label="Views"
                  value={stats?.storeViews || 0}
                  icon={<RiEyeLine />}
                />
                <KpiCard
                  label="Products"
                  value={stats?.totalProducts || 0}
                  icon={<RiArchiveLine />}
                />
                <KpiCard
                  label="Orders"
                  value={stats?.totalOrders || 0}
                  icon={<RiPulseLine />}
                />
              </>
            )}
          </div>

          {visibleQuickActions.length > 0 && (
          <Card className={dashboardStyles.sectionCard}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {visibleQuickActions.map(({ label, href, icon: Icon }) => (
                <Link
                  key={href}
                  to={href}
                  className="group flex items-center gap-3 rounded-2xl bg-[#F5F5F3] px-3.5 py-3 transition-colors hover:bg-[#EFEFEA]"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    {label}
                  </span>
                  <RiArrowRightLine className="ml-auto h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </Card>
          )}

          {hasPermission("invoices") && (
          <Card className={dashboardStyles.listSectionCard}>
            <div className="flex items-center justify-between">
              <p className={dashboardStyles.sectionTitle}>Recent Invoices</p>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs font-semibold text-primary/70 hover:text-primary"
              >
                <Link to="/dashboard/invoices">
                  View All
                  <RiArrowRightLine className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
            <div className={dashboardStyles.listContainer}>
              {invoicesLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-2xl px-3.5 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 shrink-0 rounded-xl" />
                      <div>
                        <Skeleton className="mb-1.5 h-3 w-28" />
                        <Skeleton className="h-2.5 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-3 w-16" />
                  </div>
                ))
              ) : recentInvoices?.data?.length ? (
                recentInvoices.data.map((invoice) => (
                  <Link
                    key={invoice._id}
                    to={`/dashboard/invoices/${invoice._id}`}
                    className={dashboardStyles.listItem}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={dashboardStyles.listItemIcon}>
                        <RiFileTextLine
                          className={dashboardStyles.listItemIconSvg}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold leading-tight">
                          {invoice.invoiceNumber}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {invoice.client.name}
                        </div>
                      </div>
                    </div>
                    <div className="ml-2 shrink-0 text-right">
                      <div className="font-heading text-sm font-semibold leading-tight">
                        {formatCurrency(invoice.total)}
                      </div>
                      <Badge
                        className={`mt-0.5 capitalize ${getStatusColor(invoice.status)}`}
                      >
                        {invoice.status}
                      </Badge>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center px-4 py-8">
                  <div className={dashboardStyles.emptyIcon}>
                    <RiFileTextLine className="h-4 w-4" />
                  </div>
                  <p className={dashboardStyles.emptyTitle}>No invoices yet</p>
                  <p className={dashboardStyles.emptyDescription}>
                    Create your first invoice to bill customers and track
                    payments.
                  </p>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full px-4 text-xs font-semibold"
                  >
                    <Link to="/dashboard/invoices/new">
                      <RiAddLine className="mr-1.5 h-3.5 w-3.5" />
                      Create Invoice
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </Card>
          )}

          <Card className={cn(dashboardStyles.sectionCard, "space-y-4")}>
            <div className="flex items-center justify-between">
              <div>
                <p className={dashboardStyles.sectionTitle}>
                  Performance Summary
                </p>
                <p className={dashboardStyles.sectionSubtitle}>Last 30 days</p>
              </div>
              {!perfLoading && performance && (
                <div className="flex items-center gap-1 text-sm font-semibold">
                  {(performance.growthRate ?? 0) >= 0 ? (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <RiArrowRightUpBoxFill className="h-4 w-4" />+
                      {performance.growthRate.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-500">
                      <RiArrowRightDownBoxFill className="h-4 w-4" />
                      {performance.growthRate.toFixed(1)}%
                    </span>
                  )}
                  <span className="text-xs font-medium text-muted-foreground">
                    vs prev period
                  </span>
                </div>
              )}
            </div>

            {perfLoading ? (
              <Skeleton className="h-[180px] w-full rounded-2xl" />
            ) : (
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={performance?.salesTimeline ?? []}
                    margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="perfRevenueGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#451892"
                          stopOpacity={0.28}
                        />
                        <stop
                          offset="95%"
                          stopColor="#451892"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#efece6"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) =>
                        new Date(v).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      }
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                      tick={{ fill: "#9ca3af" }}
                    />
                    <YAxis
                      tickFormatter={(v: number) =>
                        v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                      }
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#9ca3af" }}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        formatCurrency(value),
                        "Revenue",
                      ]}
                      labelFormatter={(label) =>
                        new Date(label).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                        })
                      }
                      contentStyle={{
                        fontSize: "12px",
                        borderRadius: "16px",
                        border: "1px solid rgba(69,24,146,0.08)",
                        boxShadow: "0 16px 40px -24px rgba(69,24,146,0.2)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#451892"
                      strokeWidth={2}
                      fill="url(#perfRevenueGrad)"
                      dot={false}
                      activeDot={{ r: 4, fill: "#451892" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 pt-1">
              {perfLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(dashboardStyles.statTile, "space-y-1")}
                  >
                    <Skeleton className="mb-2 h-3 w-20" />
                    <Skeleton className="h-6 w-24" />
                  </div>
                ))
              ) : (
                <>
                  <div className={dashboardStyles.statTile}>
                    <p className={dashboardStyles.statTileLabel}>
                      Avg. Order Value
                    </p>
                    <p className={dashboardStyles.statTileValue}>
                      {formatCurrency(performance?.averageOrderValue ?? 0)}
                    </p>
                  </div>
                  <div className={dashboardStyles.statTile}>
                    <p className={dashboardStyles.statTileLabel}>
                      Conversion Rate
                    </p>
                    <p className={dashboardStyles.statTileValue}>
                      {(performance?.conversionRate ?? 0).toFixed(1)}%
                    </p>
                  </div>
                  <div className={dashboardStyles.statTile}>
                    <p className={dashboardStyles.statTileLabel}>Growth Rate</p>
                    <p
                      className={cn(
                        dashboardStyles.statTileValue,
                        (performance?.growthRate ?? 0) >= 0
                          ? "text-emerald-600"
                          : "text-red-500",
                      )}
                    >
                      {(performance?.growthRate ?? 0) >= 0 ? "+" : ""}
                      {(performance?.growthRate ?? 0).toFixed(1)}%
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          {currentStore && (
            <Card className={dashboardStyles.sectionCard}>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10">
                    <RiLink className="h-4 w-4 text-primary" />
                  </div>
                  <p className={dashboardStyles.sectionTitle}>Your store link</p>
                </div>
                {hasShareableStore ? (
                  <button
                    onClick={handleCopyLink}
                    className="group flex w-full items-center justify-between gap-2 rounded-2xl border border-dashed border-primary/15 bg-[#f6f4ef] px-3.5 py-2.5 transition-colors hover:bg-[#f3f1ec]"
                  >
                    <span className="truncate font-mono text-xs text-muted-foreground">
                      {storeLink}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
                      {copyStatus === "copied" ? (
                        <span className="text-emerald-500">Copied!</span>
                      ) : (
                        <>
                          <RiFileCopyLine className="h-3 w-3" />
                          Copy
                        </>
                      )}
                    </span>
                  </button>
                ) : (
                  <p className="text-xs font-medium text-muted-foreground">
                    Add a custom URL in settings to unlock your public
                    storefront.
                  </p>
                )}
              </div>
            </Card>
          )}

          <Card className={dashboardStyles.listSectionCard}>
            <div className="flex items-center justify-between">
              <p className={dashboardStyles.sectionTitle}>Recent Orders</p>
              {hasPermission("orders") && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs font-semibold text-primary/70 hover:text-primary"
              >
                <Link to="/dashboard/orders">
                  View All
                  <RiArrowRightLine className="h-3.5 w-3.5" />
                </Link>
              </Button>
              )}
            </div>
            <div className={dashboardStyles.listContainer}>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-2xl px-3.5 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 shrink-0 rounded-xl" />
                      <div>
                        <Skeleton className="mb-1.5 h-3 w-28" />
                        <Skeleton className="h-2.5 w-20" />
                      </div>
                    </div>
                    <div className="text-right">
                      <Skeleton className="mb-1.5 ml-auto h-3 w-16" />
                      <Skeleton className="ml-auto h-4 w-16 rounded-full" />
                    </div>
                  </div>
                ))
              ) : stats?.recentOrders?.length > 0 ? (
                stats.recentOrders.slice(0, 5).map((order: RecentOrder) => (
                  <div key={order.id} className={dashboardStyles.listItem}>
                    <div className="flex items-center gap-3">
                      <div className={dashboardStyles.listItemIcon}>
                        <RiShoppingCartLine
                          className={dashboardStyles.listItemIconSvg}
                        />
                      </div>
                      <div>
                        <div className="text-sm font-semibold leading-tight">
                          {order.orderNumber}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {order.customer}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-sm font-semibold leading-tight">
                        {formatCurrency(order.amount)}
                      </div>
                      <Badge
                        className={`mt-0.5 ${getStatusColor(order.status)}`}
                      >
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center px-4 py-8">
                  <div className={dashboardStyles.emptyIcon}>
                    <RiShoppingCartLine className="h-4 w-4" />
                  </div>
                  <p className={dashboardStyles.emptyTitle}>No orders yet</p>
                  <p className={dashboardStyles.emptyDescription}>
                    Share your store link to start receiving orders.
                  </p>
                  {hasPermission("products") && (
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full px-4 text-xs font-semibold"
                  >
                    <Link to="/dashboard/products/new">
                      <RiAddLine className="mr-1.5 h-3.5 w-3.5" />
                      Add First Product
                    </Link>
                  </Button>
                  )}
                </div>
              )}
            </div>
          </Card>

          <Card className={dashboardStyles.listSectionCard}>
            <div className="flex items-center justify-between">
              <p className={dashboardStyles.sectionTitle}>Top Products</p>
              {hasPermission("products") && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs font-semibold text-primary/70 hover:text-primary"
              >
                <Link to="/dashboard/products">
                  View All
                  <RiArrowRightLine className="h-3.5 w-3.5" />
                </Link>
              </Button>
              )}
            </div>
            <div className={dashboardStyles.listContainer}>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-2xl px-3.5 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 shrink-0 rounded-xl" />
                      <div>
                        <Skeleton className="mb-1.5 h-3 w-32" />
                        <Skeleton className="h-2.5 w-20" />
                      </div>
                    </div>
                    <div className="text-right">
                      <Skeleton className="mb-1.5 ml-auto h-3 w-16" />
                      <Skeleton className="ml-auto h-2.5 w-12" />
                    </div>
                  </div>
                ))
              ) : stats?.topProducts?.length > 0 ? (
                stats.topProducts.slice(0, 5).map((product: TopProduct) => (
                  <div
                    key={product.id}
                    className={dashboardStyles.listItemMuted}
                  >
                    <div className="flex items-center gap-3">
                      <div className={dashboardStyles.listItemIcon}>
                        <RiArchiveLine
                          className={dashboardStyles.listItemIconSvg}
                        />
                      </div>
                      <div>
                        <div className="max-w-[130px] truncate text-sm font-semibold leading-tight">
                          {product.name}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {product.sales > 1
                            ? `${product.sales} sales`
                            : `${product.sales} sale`}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold leading-tight">
                        {formatCurrency(product.revenue)}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        Revenue
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center px-4 py-8">
                  <div className={dashboardStyles.emptyIcon}>
                    <RiArchiveLine className="h-4 w-4" />
                  </div>
                  <p className={dashboardStyles.emptyTitle}>
                    {(stats?.totalProducts ?? 0) > 0
                      ? "No sales yet"
                      : "No products yet"}
                  </p>
                  <p className={dashboardStyles.emptyDescription}>
                    {(stats?.totalProducts ?? 0) > 0
                      ? "Product sales will appear here once orders are placed in this period."
                      : "Add products to start tracking sales performance."}
                  </p>
                  {(stats?.totalProducts ?? 0) === 0 && (
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-full px-4 text-xs font-semibold"
                    >
                      <Link to="/dashboard/products/new">
                        <RiAddLine className="mr-1.5 h-3.5 w-3.5" />
                        Add First Product
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
